/**
   * Will scope the components script to the DOM element of the component.
   *
   * @param {string} name The name of the component
   * @param {element} component The component DOM element
   */
const register = (name, component) => {
  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll(`[data-component=${name}]`).forEach(el => component(el))
  })
}

/**
   * Loads the prefix of the component. Requires data-prefix inside DOM.
   *
   * @param {element} component The component element
   * @return {string} The prefix of the element
   */
const getPrefix = (component) => {
  return component.dataset.prefix
}

/**
   * Will attach scripts from an array (scripts) to the beginning of an element.
   * It is feasible for big libraries that should only be loaded on special pages.
   *
   * @param {array} scripts The array of script paths
   * @param {element} el The element where the scripts will be attached to
   * @param {function} attachRecall The function that should be called on success
   */
const attachScripts = (scripts, el, attachRecall) => {
  const attachPromises = (src, position) => {
    return new Promise(function (resolve, reject) {
      const script = document.createElement('script')
      script.src = src
      script.onload = resolve
      script.onerror = reject
      el.insertBefore(script, el.childNodes[position])
    })
  }

  let result = scripts.reduce((accumulatorPromise, currentValue, index) => {
    return accumulatorPromise.then(() => {
      return attachPromises(currentValue, index)
    })
  }, Promise.resolve())

  result
    .then(attachRecall)
    .catch((e) => {
      console.log('Error on attaching scripts: ', e)
    })
}

/**
   * Checks if the provided element is visible in the viewport. Returns true when it is.
   *
   * @param {element} el The element that is checked
   */
const isInViewport = (el) => {
  var bounding = el.getBoundingClientRect()
  return (
    bounding.top >= 0 &&
    bounding.left >= 0 &&
    bounding.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
    bounding.right <= (window.innerWidth || document.documentElement.clientWidth)
  )
}

const registerGlobalVariable = (varName, initialValue) => {
  window[varName] = {
    value: initialValue,
    aListener: function (val) {},
    set setVar (val) {
      this.value = val
      this.aListener(val)
    },
    get getVar () {
      return this.value
    },
    registerListener: function (listener) {
      this.aListener = listener
    }
  }
}

const storageAvailable = type => {
  let storage
  try {
    storage = window[type]
    let x = '__storage_test__'
    storage.setItem(x, x)
    storage.removeItem(x)
    return true
  } catch (e) {
    return (
      e instanceof DOMException &&
      // everything except Firefox
      (e.code === 22 ||
        // Firefox
        e.code === 1014 ||
        // test name field too, because code might not be present
        // everything except Firefox
        e.name === 'QuotaExceededError' ||
        // Firefox
        e.name === 'NS_ERROR_DOM_QUOTA_REACHED') &&
      // acknowledge QuotaExceededError only if there's something already stored
      storage &&
      storage.length !== 0
    )
  }
}

export {
  register,
  getPrefix,
  attachScripts,
  isInViewport,
  registerGlobalVariable,
  storageAvailable
}
